import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { APP_BASE_HREF } from '@angular/common';
import { SharedGatewaysModule } from '@cigna/shared/angular/gateways-util';
import { UiIconModule } from '@cigna/shared/angular/icon-ui';
import { FeaturesModule } from '@cigna/shared/angular/features-feature';
import { MatIconModule } from '@angular/material/icon';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CignaForBrokersShellFeatureModule } from '@cigna/cigna-for-brokers/shell/feature';
import {
  manifestAccessorFactory,
  CignaForBrokersSharedUtilModule,
  CONFIG,
} from '@cigna/cigna-for-brokers/shared/util';
import {
  gatewaysProviders,
  extGatewaysProviders,
} from '@cigna/cigna-for-brokers/shared/gateways';
import { TranslateModule } from '@ngx-translate/core';

import {
  ResourceContentDataAccessModule,
  CONTENT_BUNDLE_MANIFEST_ACCESSOR,
  contentBundleProviders,
} from '@cigna/shared/angular/resource-content-data-access';

import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { LogEventLevel } from '@cigna/shared/angular/logging-util';
import {
  TEALEAF_CONFIG,
  SharedTealeafUtilModule,
} from '@cigna/shared/angular/tealeaf-util';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { RouterModule } from '@angular/router';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CoreWindowModule } from '@cigna/shared/angular/core/window-util';
import { CookieService } from 'ngx-cookie-service';
import { routerReducer } from '@ngrx/router-store';
import { CoreRouterStateModule } from '@cigna/shared/angular/core/router-state-data-access';
import { IConfig } from 'ngx-mask';
import { UiModalModule } from '@cigna/shared/angular/modal-ui';
import { ResourceContentUiModule } from '@cigna/shared/angular/resource-content-ui';
import { MatDialogModule } from '@angular/material/dialog';
import { ImplicitFlowService } from '@cigna/shared/angular/auth/oidc-util/implicit';
import { AuthDataAccessModule } from '@cigna/shared/angular/auth-data-access';
import { CignaForBrokersAuthDataAccessModule } from '@cigna/cigna-for-brokers/auth/data-access';
import { LoginFacade } from '@cigna/cigna-for-brokers/login-data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CaAnalyticsUtilModule } from '@cigna/cigna-for-brokers/shared/analytics-util';
import { AnalyticsDataAccessModule } from '@cigna/shared/angular/analytics-data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CignaForBrokersShellUiModule } from '@cigna/cigna-for-brokers/shell/ui';
import { AppErrorComponent } from './app-error/app-error.component';
import { OktaFlowService } from '@cigna/shared/angular/auth/oidc-util/okta';
import { timeoutPopupProviders } from '@cigna/shared/angular/timeout-popup-feature';
import {
  SharedGoogleMapsUtilModule,
  cignaForEmployersGoogleMapsConfig,
} from '@cigna/shared/angular/google-maps-util';

export function cdnBaseUrlFactory(_baseHref: string) {
  return environment.production
    ? `${environment.api.sharedGateways.cdn}spa/cigna-for-brokers/`
    : '';
}

export function getSecureUris() {
  if (environment.features.isOKTASecureUriEnabled) {
    return environment.oidc.secureUris;
  }
  return environment.oidc.nonsecureUris;
}

const maskConfig: Partial<IConfig> = {
  validation: true,
};
@NgModule({
  declarations: [AppComponent, AppErrorComponent],
  providers: [
    { provide: CONFIG, useValue: environment },
    {
      provide: TEALEAF_CONFIG,
      useValue: {
        logLevel: LogEventLevel.Error,
        logDescription: 'Cignaaccess Javascript Error',
      },
    },
    {
      provide: APP_BASE_HREF,
      useValue: '/',
    },
    { provide: ImplicitFlowService, useClass: ImplicitFlowService },
    ...gatewaysProviders(environment.api.identity),
    ...extGatewaysProviders(environment.api.extUrls),
    {
      provide: CONTENT_BUNDLE_MANIFEST_ACCESSOR,
      useFactory: manifestAccessorFactory,
      deps: [],
    },
    {
      provide: `dontAllowAnalyticsForCurrentRls`,
      useValue: environment.dontAllowAnalyticsForCurrentRls,
    },
    CookieService,
    ...contentBundleProviders(environment.contentBundle),
    ...timeoutPopupProviders(
      environment.sessionTimeoutPopup,
      (okta, login) => ({
        onLogout: () => login.logout('inactive'),
        onKeepAlive: () => okta.authenticate(),
      }),
      OktaFlowService,
      LoginFacade,
    ),
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    MatIconModule,
    UiModalModule,
    ResourceContentUiModule,
    RouterModule,
    CignaForBrokersShellFeatureModule,
    ResourceContentDataAccessModule,
    AuthDataAccessModule,
    CignaForBrokersAuthDataAccessModule.getWithConfig(
      environment.features.isOktaLoginEnabled,
      environment.features.isOktaLoginEnabled
        ? {
            ...environment.api.identity,
            ...environment.okta,
            secureUris: environment.oidc.secureUris,
            publicUris: environment.oidc.publicUris,
            noRefreshUris: environment.oidc.noRefreshUris,
          }
        : environment.api.identity,
    ),
    TranslateModule.forRoot(),
    UiIconModule.forRoot({
      factory: cdnBaseUrlFactory,
      deps: [APP_BASE_HREF],
    }),
    StoreModule.forRoot(
      {
        router: routerReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
      },
    ),
    SharedGoogleMapsUtilModule.forRoot({
      options: cignaForEmployersGoogleMapsConfig,
    }),
    CoreRouterStateModule,
    EffectsModule.forRoot([]),
    CoreWindowModule.forRoot(),
    !environment.production
      ? StoreDevtoolsModule.instrument({ connectInZone: true })
      : [],
    SharedGatewaysModule.withConfig(environment.api.sharedGateways),
    SharedTealeafUtilModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    FeaturesModule,
    CaAnalyticsUtilModule,
    AnalyticsDataAccessModule,
    CignaForBrokersSharedUtilModule,
    MatDialogModule,
    CignaForBrokersShellUiModule,
  ],
})
export class AppModule {}
